import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "../lib/constants";

const navItems = [
    { to: "/", label: "Forside", end: true },
    { to: "/projekter", label: "Projekter" },
    { to: "/om-os", label: "Om os" },
    { to: "/kontakt", label: "Kontakt" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-all duration-300 backdrop-blur-xl ${
                scrolled
                    ? "bg-[#F9F6F0]/92 border-b border-[#D1C7B7]/70 shadow-[0_8px_24px_-20px_rgba(44,36,27,0.3)]"
                    : "bg-[#F9F6F0]/80 border-b border-[#D1C7B7]/40"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between py-4 md:py-5">
                <Link
                    to="/"
                    data-testid="header-logo-link"
                    className="flex items-center gap-3 group"
                >
                    <span className="relative w-11 h-11 md:w-12 md:h-12 overflow-hidden rounded-sm ring-1 ring-[#D1C7B7] bg-[#F0EBE1]">
                        <img
                            src={COMPANY.logo}
                            alt="Fyr og Finér logo"
                            className="w-full h-full object-cover"
                        />
                    </span>
                    <span className="flex flex-col leading-none">
                        <span className="font-serif text-[1.3rem] md:text-[1.55rem] text-[#2C241B] tracking-tight">
                            Fyr <span className="italic">&amp;</span> Finér
                        </span>
                        <span className="overline mt-1 text-[0.62rem] md:text-[0.65rem]">
                            Tømrerfirmaet · ApS
                        </span>
                    </span>
                </Link>

                <nav
                    data-testid="desktop-nav"
                    className="hidden md:flex items-center gap-10"
                >
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `text-sm tracking-wide uppercase font-medium transition-colors link-reveal ${
                                    isActive
                                        ? "text-[#2F483A]"
                                        : "text-[#2C241B]/70 hover:text-[#2C241B]"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        href={COMPANY.phoneHref}
                        data-testid="header-phone-link"
                        className="inline-flex items-center gap-2 bg-[#2F483A] hover:bg-[#223429] text-[#F9F6F0] px-5 py-3 text-xs uppercase tracking-[0.2em] font-medium transition-colors rounded-sm"
                    >
                        <Phone size={14} strokeWidth={2} />
                        {COMPANY.phone}
                    </a>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden p-2 text-[#2C241B]"
                    aria-label="Åbn menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-nav"
                    className="md:hidden border-t border-[#D1C7B7]/60 bg-[#F9F6F0]"
                >
                    <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={({ isActive }) =>
                                    `font-serif text-2xl ${
                                        isActive
                                            ? "text-[#2F483A]"
                                            : "text-[#2C241B]"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <a
                            href={COMPANY.phoneHref}
                            data-testid="mobile-phone-link"
                            className="mt-2 inline-flex items-center gap-2 bg-[#2F483A] text-[#F9F6F0] px-5 py-3 text-xs uppercase tracking-[0.2em] font-medium rounded-sm w-fit"
                        >
                            <Phone size={14} /> {COMPANY.phone}
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;
